
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import RuleTabPanel from '@components/settings/rule/RuleTabPanel.vue';
import { RuleTriggerEnum, RuleV2Mutation, TripLog, Zone } from '@clients/myTrips';
import { namespace } from 'vuex-class';
import AppButton from '@components/new-styleguide/AppButton.vue';
import MultipleZoneSelect from '@components/new-styleguide/form/MultipleZoneSelect.vue'
import MultipleVehicleSelect from '@components/new-styleguide/form/MultipleVehicleSelect.vue';

const SettingsModule = namespace('Settings');
const TriplogModule = namespace('Triplog');

@Component({
  name: 'RuleTabTrigger',
  components: {
    AppButton,
    RuleTabPanel,
    MultipleZoneSelect,
    MultipleVehicleSelect
  }
})
export default class RuleTabTrigger extends Vue {
  @Prop({ required: true })
  private value: RuleV2Mutation;

  @Prop({ default: false })
  private loading: boolean;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @SettingsModule.Getter('getZones')
  private zones: Zone[];

  @TriplogModule.Getter('getVehicles')
  private vehicles: TripLog[];

  get mappedVehicles() {
    return this.value.vehicleIds.map(v => this.vehicles.find(v1 => v1.vehicle.id == v));
  }

  get mappedZones() {
    return this.value.zoneIds.map(z => this.zones.find(z1 => z1.id == z));
  }

  get tripStart() {
    return this.value.triggers.includes(RuleTriggerEnum.TripStart);
  }

  get tripEnd() {
    return this.value.triggers.includes(RuleTriggerEnum.TripEnd);
  }

  get renderCarSelect() {
    return this.vehicles && this.vehicles.length > 1;
  }

  public handleFromInput(time: string) {
    if (time) {
      this.inputTimeframe('from', time);
    } else {
      this.clearTimeframe();
    }
  }

  public handleToInput(time: string) {
    if (time) {
      this.inputTimeframe('until', time);
    } else {
      this.clearTimeframe();
    }
  }

  private handleVehicleArrayChange(array: Array<TripLog>) {
    this.input('vehicleIds', array.map(t => t.vehicle.id));
  }

  private handleZoneArrayChange(array: Array<Zone>) {
    this.input('zoneIds', array.map(z => z.id));
  }

  private handleTripStartChange(e: { target: HTMLInputElement }) {
    if (e.target.checked && !this.tripStart) {
      this.input('triggers', [RuleTriggerEnum.TripStart, ...this.value.triggers]);
    } else if (!e.target.checked && this.tripStart) {
      const copy = [...this.value.triggers];
      copy.splice(copy.findIndex(v => v === RuleTriggerEnum.TripStart), 1);
      this.input('triggers', copy);
    }
  }

  private handleTripEndChange(e: { target: HTMLInputElement }) {
    if (e.target.checked && !this.tripEnd) {
      this.input('triggers', [RuleTriggerEnum.TripEnd, ...this.value.triggers]);
    } else if (!e.target.checked && this.tripEnd) {
      const copy = [...this.value.triggers];
      copy.splice(copy.findIndex(v => v === RuleTriggerEnum.TripEnd), 1);
      this.input('triggers', copy);
    }
  }

  private clearTimeframe() {
    this.input('triggerTimeframe', null);
  }

  private inputTimeframe(name: string, value) {
    const copy = {...this.value};

    if (!this.value.triggerTimeframe) {
      copy.triggerTimeframe = { from: null, until: null };
    }
    copy.triggerTimeframe[name] = value;

    this.$emit('input', copy);
  }

  private input(name: string, value) {
    const copy = {...this.value};

    copy[name] = value;

    this.$emit('input', copy);
  }
}
